import './App.css';
import {useState,Suspense,useEffect} from "react";
import {BrowserRouter as Router,Routes,Route} from "react-router-dom";
import keycloak from "./authentication/Keycloak.js";
import {ReactKeycloakProvider} from "@react-keycloak/web";
import {ThemeProvider} from '@mui/material/styles';
import {Snackbar,Alert} from '@mui/material';
import {theme} from './components/Theme.js'
import {SnackbarProvider} from './components/SnackbarContext.js';
import {LoadingProvider} from './components/LoadingContext.js';
import {ConfirmProvider} from './components/ConfirmContext.js';
import {ApplicationProvider} from './components/ApplicationContext.js';
import {NotificationsProvider} from './components/Notification/NotificationsProvider.js';
import Background from "./components/Background.js";
import SessionExpiryDialog from "./components/SessionExpiryDialog.js";

import Home from '../custom/components/Home.js';
import Header from "../custom/components/Header.js";
import Footer from "../custom/components/Footer.js";

import PrivateRoute from "./components/PrivateRoute.js";
import {authorization} from './integration/Api.js';
import {consoleLog} from './utilities/Utilities.js';
import {getEntityComponent} from '../custom/entities/EntitiyComponent.js'
const file='App.js';
const SSO_SESSION_WARN_BEFORE_MS=5*60*1000;

function App(){
  const [userData,setUserData]=useState({});
  const [modules,setModules]=useState([]);
  const [roleConfig,setRoleConfig]=useState(null);
  const [sessionExpiresAt,setSessionExpiresAt]=useState(null);
  const [showSessionWarning,setShowSessionWarning]=useState(false);
  const [authError,setAuthError]=useState(null);

  // Keep the Keycloak SSO session alive while the user is actively working.
  // The SSO idle timeout (4 min) is shorter than the access token lifespan (30 min),
  // so onTokenExpired alone would not refresh often enough to prevent idle expiry.
  // Every 2 minutes we check if there was recent UI activity; if so, we call
  // updateToken(-1) to force a refresh-token exchange, which resets the SSO idle
  // timer on the server. Errors are swallowed by Keycloak.js and surface via
  // onAuthRefreshError → performSessionExpiredRedirect if the session truly expired.
  const SSO_KEEP_ALIVE_INTERVAL_MS=2*60*1000;
  useEffect(()=>{
    const lastActivityRef={current:null};

    const markActivity=()=>{lastActivityRef.current=Date.now();};
    const events=['mousemove','keydown','click','scroll','touchstart'];
    events.forEach(evt=>document.addEventListener(evt,markActivity,{passive:true}));

    const interval=setInterval(()=>{
      if(!keycloak.authenticated) return;
      if(!lastActivityRef.current) return;
      const inactiveMs=Date.now()-lastActivityRef.current;
      // User was active within this interval window — extend the SSO session.
      if(inactiveMs<SSO_KEEP_ALIVE_INTERVAL_MS){
        consoleLog({file:file,context:'ssoKeepAlive',arg_name:'inactiveMs',arg_value:inactiveMs});
        keycloak.updateToken(-1);
      }
    },SSO_KEEP_ALIVE_INTERVAL_MS);

    return()=>{
      events.forEach(evt=>document.removeEventListener(evt,markActivity));
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Warn the user SSO_SESSION_WARN_BEFORE_MS before the hard SSO Session Max wall.
  // sessionExpiresAt is (re-)set on every onAuthSuccess, so a re-login resets the timer.
  useEffect(()=>{
    if(!sessionExpiresAt) return;
    const delay=sessionExpiresAt-Date.now()-SSO_SESSION_WARN_BEFORE_MS;
    if(delay<=0){
      setShowSessionWarning(true);
      return;
    }
    consoleLog({file:file,context:'sessionMaxWarning',arg_name:'warnInMs',arg_value:delay});
    const timer=setTimeout(()=>setShowSessionWarning(true),delay);
    return()=>clearTimeout(timer);
  },[sessionExpiresAt]);

  const performSessionExpiredRedirect=()=>{
    consoleLog({file:file,context:'performSessionExpiredRedirect',arg_name:'returnUrl',arg_value:localStorage.getItem('sessionExpiredReturnUrl')||window.location.href});
    sessionStorage.setItem("logCounter",0);
    localStorage.setItem('sessionExpiredMessage','SessionExpired');
    if(!localStorage.getItem('sessionExpiredReturnUrl')){
      localStorage.setItem('sessionExpiredReturnUrl',window.location.href);
    }
    // Distinct previousEvent: onReady will show Home with "Session Expired" without
    // auto-redirecting. User clicks Login; Header passes sessionExpiredReturnUrl back.
    localStorage.setItem('previousEvent','onSessionExpiredLogout');
    localStorage.setItem('activeTaskId',JSON.stringify(null));
    keycloak.logout({redirectUri:window.location.origin});
  };

  const getAuthorization=()=>{
    // Capture auth_time synchronously — keycloak.tokenParsed is current at call time.
    const authTime=keycloak.tokenParsed?.auth_time;
    authorization()
    .then((response)=>{
        const data=JSON.parse(response);
        consoleLog({file:file,context:'getAuthorization - authorization',arg_name:'data',arg_value:data});
        if('error' in data){
          consoleLog({file:file,context:'getAuthorization',arg_name:'authError',arg_value:data.error});
          setAuthError('Authorization failed. Please try again or contact support.');
        }
        else{
          if('userData' in data){
            setUserData(data.userData);
          }
          if('modulesConfig' in data){
            setModules(data.modulesConfig);
          }
          if('roleConfig' in data){
            setRoleConfig(data.roleConfig);
          }
          if('session_config' in data && authTime){
            const expiresAt=authTime*1000+data.session_config.sso_session_max_ms;
            setSessionExpiresAt(expiresAt);
            setShowSessionWarning(false);
            consoleLog({file:file,context:'getAuthorization',arg_name:'ssoExpiresAt',arg_value:new Date(expiresAt).toISOString()});
            if(data.session_config.warnings?.length){
              data.session_config.warnings.forEach(w=>consoleLog({file:file,context:'getAuthorization - KC sanity warning',arg_name:'warning',arg_value:w}));
            }
          }
        }
    });
  }

  const callbackKeyCloack=(e)=>{
    if(e==='onAuthSuccess'){
      consoleLog({file:file,context:'onAuthSuccess',arg_name:'',arg_value:null});
      localStorage.setItem('sessionExpiredMessage','');
      localStorage.setItem('sessionExpiredReturnUrl','');
      localStorage.setItem('previousEvent','onAuthSuccess');
      getAuthorization();
    }
    else if(e==='onAuthRefresh'){
      consoleLog({file:file,context:'onAuthRefresh',arg_name:'',arg_value:null});
    }
    else if(e==='onTokenExpired'){
      consoleLog({file:file,context:'onTokenExpired',arg_name:'returnUrl',arg_value:window.location.href});
      localStorage.setItem('sessionExpiredReturnUrl',window.location.href);
      // Proactively refresh while the SSO session is still alive. If the SSO session
      // has already expired this will fail and onAuthRefreshError → logout fires instead.
      keycloak.updateToken(30);
    }
    else if(e==='onAuthRefreshError'){
      consoleLog({file:file,context:'onAuthRefreshError',arg_name:'',arg_value:null});
      performSessionExpiredRedirect();
    }
    else if(e==='onAuthLogout'){
      consoleLog({file:file,context:'onAuthLogout',arg_name:'',arg_value:null});
      performSessionExpiredRedirect();
    }
    else if(e==='onReady'){
      const prevEvent=localStorage.getItem('previousEvent');
      consoleLog({file:file,context:'onReady',arg_name:'authenticated',arg_value:{authenticated:keycloak.authenticated,previousEvent:prevEvent}});
      if(prevEvent==='onAuthRefreshError'){
        // Network-level refresh failure (not idle expiry) — auto-redirect to login.
        localStorage.setItem('previousEvent','onReady');
        if(!keycloak.authenticated){
          const returnUrl=localStorage.getItem('sessionExpiredReturnUrl')||window.location.href;
          keycloak.login({redirectUri:returnUrl});
        }
      }
      else if(prevEvent==='onSessionExpiredLogout'){
        // Came here after an idle-expiry logout. Home shows "Session Expired".
        // User clicks Login; Header uses sessionExpiredReturnUrl as redirectUri.
      }
      else{
        localStorage.setItem('sessionExpiredMessage','');
      }
    }
  };

  const handleRenewSession=()=>{
    // prompt:'login' forces Keycloak to create a new SSO session, resetting auth_time.
    // Silent re-auth (no prompt) reuses the existing session and leaves auth_time unchanged,
    // so sessionExpiresAt would stay the same and the warning would reappear immediately.
    keycloak.login({redirectUri:window.location.href,prompt:'login'});
  };

  return (
    <ThemeProvider theme={theme({userData,roleConfig})}>
      {authError&&(
        <Snackbar open={!!authError} autoHideDuration={8000} onClose={()=>setAuthError(null)} anchorOrigin={{vertical:'top',horizontal:'center'}}>
          <Alert onClose={()=>setAuthError(null)} variant="filled" severity="error" sx={{width:'100%',fontSize:'1rem'}}>
            {authError}
          </Alert>
        </Snackbar>
      )}
      <LoadingProvider>
        <SnackbarProvider>
          <Background/>
          <div className="App">
            <ReactKeycloakProvider authClient={keycloak} onEvent={callbackKeyCloack}>
              <ApplicationProvider userData={userData}>
                <SessionExpiryDialog
                  showSessionWarning={showSessionWarning}
                  sessionExpiresAt={sessionExpiresAt}
                  onRenew={handleRenewSession}
                  onDismiss={()=>setShowSessionWarning(false)}
                />
                <ConfirmProvider>
                  <Router>
                    <NotificationsProvider>
                      <Header menuItems={modules} userData={userData}/>
                      <Routes>
                        <Route exact path="/home" element={<Home roleConfig={roleConfig}/>}/>
                        <Route exact path="/" element={<Home roleConfig={roleConfig}/>}/>
                          {modules.length!==0&&Object.values(modules?.appItems).flatMap(app=>app.menuItems).filter(module=>module.name!=="home").flatMap((module)=>Object.entries(module.routes).map(([routeKey,route])=>(
                            <Route
                              key={`${module.name}_${routeKey}`}
                              path={route.path}
                              element={
                                <Suspense fallback={<div></div>}>
                                  <PrivateRoute
                                    childComponent={getEntityComponent(`${module.name}_${routeKey}`)}
                                    authClient={keycloak}
                                    entityDetail={{route:route,name:routeKey}}
                                    userData={userData}/>
                                </Suspense>
                              }/>
                            ))
                          )}
                        <Route path="*" element={<Home roleConfig={roleConfig}/>}/>
                      </Routes>
                      <Footer userData={userData}></Footer>
                    </NotificationsProvider>
                  </Router>
                </ConfirmProvider>
              </ApplicationProvider>
            </ReactKeycloakProvider>
          </div>
        </SnackbarProvider>
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App;
